/* eslint-disable @typescript-eslint/member-ordering */
import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Observable, Observer, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TimeInterval } from 'src/shared/models/module-manager/module-manager-settings.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { DateRangeSelectorService } from '../date-range-selector/date-range-selector.service';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { CVLocalizationService } from '../services/cv-localization.service';

@Component({
    selector: 'cv-interval-selector',
    templateUrl: './interval-selector.component.html',
    styleUrls: ['./interval-selector.component.scss']
})
export class IntervalSelectorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() settingsManager: SlideoutSettingsManager;
    @Input() showFifteenMinute = false;
    @Input() stateKey: string;
    @Input() layout: 'default' | 'reverse' = 'default';
    @Input() useGrid = true;
    @Input() hideDisabled = false;

    /** Disable the "Amount" selector for these intervals. */
    intervalNoAmount = new Set([
        TimeInterval.HalfHour,
        TimeInterval.FifteenMinute,
        TimeInterval.DayOfWeek,
    ]);

    intervalAmountsMap = new Map<number, {id: number; name: string}[]>();
    intervalTypes: { id: number; order: number; name: string }[] = [];
    intervalTypesMap = new Map<number, {id: number; order: number; name: string}>();

    private _intervalChangedObserver: Observer<void>;
    private _intervalChangedObservable: Observable<void>;
    private _intervalSavePromise: Promise<any>;
    private _subscriptions: Subscription[] = [];

    constructor(
        private config: CvConfigurationService,
        private localizationService: CVLocalizationService,
        private dateRangeSelectorService: DateRangeSelectorService,
    ) {
        this.localizationService.GetSection("Controls");
        this.initializeIntervalMap();
    }

    get useCxOne() { return this.config.isCxOne; }
    get amountDisabled() { return this.intervalNoAmount.has(this.settingsManager.intervalType); }
    get amountHidden() { return this.hideDisabled && this.amountDisabled; }

    get intervalTypeCxOne() {
        return this.intervalTypesMap.get(this.settingsManager.intervalType);
    }
    set intervalTypeCxOne(v: { id: number; name: string; order: number}) {
        this.settingsManager.intervalType = v.id;
    }

    get intervalAmtCxOne() {
        if (!this.settingsManager.intervalAmt || !this.settingsManager.intervalType) {
            return null;
        }
        return this.intervalAmountsMap.get(this.settingsManager.intervalType)[this.settingsManager.intervalAmt - 1];
    }
    set intervalAmtCxOne(v: { id: number; name: string }) {
        this.settingsManager.intervalAmt = v.id;
    }

    ngOnInit() {
        this._subscriptions.push(
            this.settingsManager.beforeSettingsApplied.subscribe(_ => this.beforeSettingsApply())
        );

        if (this.config.isCxOne)
            this.addMetricAmtLocalizations();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showFifteenMinute) {
            this.buildIntervalTypesArr();
        }
    }

    ngOnDestroy() {
        this._subscriptions.forEach(s => s.unsubscribe());
    }

    private initializeIntervalMap() {
        for (let i = 0; i <= 7; i++) {
            this.intervalAmountsMap.set(i, []);
            const interval = this.intervalAmountsMap.get(i);
            for (let j = 1; j <= 8; j++) {
                interval.push({id: j, name: j.toString()});
            }
        }
    }

    async buildIntervalTypesArr() {
        const text = await this.localizationService.GetSection("Controls");
        this.intervalTypes = [];
        if (this.showFifteenMinute)
            this.intervalTypes.push({ order: 1, id: TimeInterval.FifteenMinute, name: text.get("Fifteen_Minute") ?? "15 minutes" });
        this.intervalTypes.push({ order: 2, id: TimeInterval.HalfHour, name: text.get("Half_Hour") ?? "30 minutes" });
        this.intervalTypes.push({ order: 3, id: TimeInterval.Hour,
            name: this.config.isCxOne ? text.get("Hourly") ?? "Hourly" : text.get("Hour") ?? "Hour"});
        this.intervalTypes.push({ order: 4, id: TimeInterval.Day,
            name: this.config.isCxOne ? text.get("Daily") ?? "Daily" : text.get("Day") ?? "Day" });
        if (!this.config.isCxOne)
            this.intervalTypes.push({ order: 5, id: TimeInterval.DayOfWeek, name: text.get("Day_Of_Week") ?? "Day Of Week" });
        this.intervalTypes.push({ order: 6, id: TimeInterval.Week,
            name: this.config.isCxOne ? text.get("Weekly") ?? "Weekly" : text.get("Week") ?? "Week" });
        this.intervalTypes.push({ order: 7, id: TimeInterval.Month,
            name: this.config.isCxOne ? text.get("Monthly") ?? "Monthly" : text.get("Month") ?? "Month" });
        if (!this.config.isCxOne)
            this.intervalTypes.push({ order: 8, id: TimeInterval.Year, name: text.get("Year") ?? "Year" });
        this.intervalTypesMap = new Map(this.intervalTypes.map(i => [i.id, i]));
    }

    addMetricAmtLocalizations() {
        let oneText = this.localizationService.getCachedText("Controls", "Singular_Hours", "{0} hour");
        let multipleText = this.localizationService.getCachedText("Controls", "Plural_Hours", "{0} hours");
        this.intervalAmountsMap.get(TimeInterval.Hour)[0].name = this.localizationService.Format(oneText, 1);
        for (let i = 1; i <= 7; i++) {
            this.intervalAmountsMap.get(TimeInterval.Hour)[i].name = this.localizationService.Format(multipleText, i + 1);
        }

        oneText = this.localizationService.getCachedText("Controls", "Singular_Days", "{0} day");
        multipleText = this.localizationService.getCachedText("Controls", "Plural_Days", "{0} days");
        this.intervalAmountsMap.get(TimeInterval.Day)[0].name = this.localizationService.Format(oneText, 1);
        for (let i = 1; i <= 7; i++) {
            this.intervalAmountsMap.get(TimeInterval.Day)[i].name = this.localizationService.Format(multipleText, i + 1);
        }

        oneText = this.localizationService.getCachedText("Controls", "Singular_Weeks", "{0} week");
        multipleText = this.localizationService.getCachedText("Controls", "Plural_Weeks", "{0} weeks");
        this.intervalAmountsMap.get(TimeInterval.Week)[0].name = this.localizationService.Format(oneText, 1);
        for (let i = 1; i <= 7; i++) {
            this.intervalAmountsMap.get(TimeInterval.Week)[i].name = this.localizationService.Format(multipleText, i + 1);
        }

        oneText = this.localizationService.getCachedText("Controls", "Singular_Months", "{0} month");
        multipleText = this.localizationService.getCachedText("Controls", "Plural_Months", "{0} months");
        this.intervalAmountsMap.get(TimeInterval.Month)[0].name = this.localizationService.Format(oneText, 1);
        for (let i = 1; i <= 7; i++) {
            this.intervalAmountsMap.get(TimeInterval.Month)[i].name = this.localizationService.Format(multipleText, i + 1);
        }
    }

    beforeSettingsApply() {
        this._intervalChangedObserver?.complete();
        this._intervalChangedObservable = null;
        this._intervalChangedObserver = null;
        if (this._intervalSavePromise) {
            this.settingsManager.waitBeforeApplying.push(this._intervalSavePromise);
        }
    }

    onIntervalChanged() {
        if (this.intervalNoAmount.has(this.settingsManager.intervalType))
            this.settingsManager.intervalAmt = 1;

        // debounce save
        if (this._intervalChangedObserver == null) {
            this._intervalChangedObservable = new Observable<void>(o => this._intervalChangedObserver = o)
                .pipe(debounceTime(1000));

            this._intervalChangedObservable.subscribe(async () => {
                this._intervalSavePromise = this.dateRangeSelectorService.saveIntervalSettings(
                    this.stateKey,
                    this.settingsManager.intervalAmt,
                    this.settingsManager.intervalType
                );
            });
        }

        this._intervalChangedObserver.next();
    }
}
